import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { decodeJwt } from 'jose';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { SilentCallbackComponent } from '../../features/silent-callback/silent-callback.component';

/** Result of one silent-SSO attempt. The shape is exhaustive so callers can
 *  switch on `status` without falling through. */
export type ToegangSsoResult =
  | { status: 'ok'; token: string }
  | { status: 'no-session' }
  | { status: 'disabled' }
  | { status: 'timeout' }
  | { status: 'error' };

interface SilentCallbackMessage {
  type: string;
  idToken: string | null;
  nonce: string | null;
  state: string | null;
  error: string | null;
}

// Tight enough that a blocked iframe (X-Frame-Options, third-party-cookie
// blocking, blocked frame-ancestors CSP) doesn't visibly stall app boot,
// loose enough that a real round-trip to Toegang's IdP fits comfortably.
const TIMEOUT_MS = 1500;
const SS_KEY_NONCE = 'onegate.toegang-sso.nonce';

const isSilentCallbackMessage = (
  data: unknown,
): data is SilentCallbackMessage =>
  typeof data === 'object' &&
  data !== null &&
  'type' in data &&
  (data as { type: unknown }).type === SilentCallbackComponent.MESSAGE_TYPE;

const readNonceClaim = (jwt: string): string | null => {
  try {
    const { nonce } = decodeJwt(jwt);
    return typeof nonce === 'string' ? nonce : null;
  } catch {
    return null;
  }
};

/**
 * Drives the OIDC `prompt=none` silent-SSO bootstrap: builds the Toegang
 * authorize URL, loads it in a hidden iframe, waits for the iframe to post
 * the result back, verifies the nonce, and hands the id_token to the
 * backend in exchange for an NT2 session JWT.
 *
 * Single-flight: concurrent calls share the same in-flight promise so
 * APP_INITIALIZER and any later opportunistic re-check never run twice.
 */
@Injectable({ providedIn: 'root' })
export class ToegangSsoService {
  readonly #http = inject(HttpClient);

  #inflight: Promise<ToegangSsoResult> | null = null;

  checkSession(): Promise<ToegangSsoResult> {
    if (this.#inflight) {
      return this.#inflight;
    }
    // Dev-only short-circuit. When set on environment.local.ts the iframe
    // round-trip is skipped — handy on hosts where Toegang doesn't whitelist
    // the silent-callback redirect_uri. Ignored in production builds so an
    // accidentally-committed stub in environment.prod.ts can't bypass the
    // real probe (the backend stays the source of truth either way — a
    // forged session token won't survive the next API call).
    const stub = environment.toegangSsoStub;
    if (stub && !environment.production) {
      return Promise.resolve(stub);
    }
    const config = environment.toegangSso;
    if (!config) {
      return Promise.resolve({ status: 'disabled' });
    }

    this.#inflight = this.#run(
      config.authUrl,
      config.clientId,
      config.silentRedirectUri,
    ).finally(() => {
      this.#inflight = null;
    });
    return this.#inflight;
  }

  async #run(
    authUrl: string,
    clientId: string,
    redirectUri: string,
  ): Promise<ToegangSsoResult> {
    const nonce = ToegangSsoService.generateNonce();
    sessionStorage.setItem(SS_KEY_NONCE, nonce);

    const url = ToegangSsoService.buildAuthUrl(
      authUrl,
      clientId,
      redirectUri,
      nonce,
    );

    const message = await ToegangSsoService.openIframeAndAwaitMessage(url);
    sessionStorage.removeItem(SS_KEY_NONCE);

    if (message === 'timeout') {
      return { status: 'timeout' };
    }
    if (message.error || !message.idToken) {
      return { status: 'no-session' };
    }

    if (readNonceClaim(message.idToken) !== nonce) {
      return { status: 'error' };
    }

    try {
      const response = await firstValueFrom(
        this.#http.post<{ session: string }>('/api/auth/toegang-sso', {
          id_token: message.idToken,
        }),
      );
      return { status: 'ok', token: response.session };
    } catch {
      return { status: 'error' };
    }
  }

  static openIframeAndAwaitMessage(
    url: string,
  ): Promise<SilentCallbackMessage | 'timeout'> {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.style.display = 'none';

      let timer: ReturnType<typeof setTimeout>;

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (!isSilentCallbackMessage(event.data)) {
          return;
        }
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
        iframe.remove();
        resolve(event.data);
      };

      timer = setTimeout(() => {
        window.removeEventListener('message', onMessage);
        iframe.remove();
        resolve('timeout');
      }, TIMEOUT_MS);

      window.addEventListener('message', onMessage);
      iframe.src = url;
      document.body.appendChild(iframe);
    });
  }

  static buildAuthUrl(
    authUrl: string,
    clientId: string,
    redirectUri: string,
    nonce: string,
  ): string {
    const params = new URLSearchParams({
      prompt: 'none',
      scope: 'openid',
      response_type: 'id_token token',
      client_id: clientId,
      redirect_uri: redirectUri,
      nonce,
      claims: JSON.stringify({
        id_token: {
          bp: null,
          licenses: null,
          email: null,
          first_name: null,
          tussenvoegsels: null,
          family_name: null,
        },
      }),
    });
    return `${authUrl}?${params.toString()}`;
  }

  static generateNonce(): string {
    return crypto.randomUUID();
  }
}
